import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchFieldMappings, deleteMapping } from "@/lib";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { getSyncModeClass, getFieldTypeClass } from "@shared/utils";
import { FieldMapping, SyncMode } from "@shared/api-types";
import { useToast } from "@/hooks/use-toast";
import FieldMappingForm from "./field-mapping-form";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Edit,
  Plus,
  Trash2,
} from "lucide-react";

interface FieldMappingsTableProps {
  onAddMapping: () => void;
}

export default function FieldMappingsTable({ onAddMapping }: FieldMappingsTableProps) {
  const [entityFilter, setEntityFilter] = useState<string>("all_entities");
  const [syncModeFilter, setSyncModeFilter] = useState<string>("all_sync_modes");
  const [currentPage, setCurrentPage] = useState(1);
  const [mappingToDelete, setMappingToDelete] = useState<FieldMapping | null>(null);
  const [mappingToEdit, setMappingToEdit] = useState<FieldMapping | null>( null);
  const itemsPerPage = 10;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["/api/field-mappings", entityFilter, syncModeFilter],
    queryFn: () =>
      fetchFieldMappings(
        entityFilter === "all_entities" ? undefined : entityFilter,
        syncModeFilter === "all_sync_modes"
          ? undefined
          : (syncModeFilter as SyncMode | undefined),
      ),
  });

  const deleteMutation = useMutation({
    mutationFn: ({
      partitionKey,
      rowKey,
    }: {
      partitionKey: string;
      rowKey: string;
    }) => deleteMapping(partitionKey, rowKey),
    onSuccess: () => {
      toast({
        title: "Mapping deleted",
        description: "The field mapping has been deleted successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/field-mappings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/field-mappings/all"] });
      queryClient.invalidateQueries({ queryKey: ["/api/field-mappings/statistics"],
      });
      setMappingToDelete(null);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to delete the field mapping.",
        variant: "destructive",
      });
    },
  });

  const handleDeleteClick = (mapping: FieldMapping) => {
    setMappingToDelete(mapping);
  };

  const handleConfirmDelete = () => {
    if (mappingToDelete) {
      deleteMutation.mutate({
        partitionKey: "FieldMapping",
        rowKey: `${mappingToDelete.entityName}_${mappingToDelete.kupField}`,
      });
    }
  };

  const handleEditClick = (mapping: FieldMapping) => {
    setMappingToEdit(mapping);
  };

  const closeDeleteDialog = () => {
    setMappingToDelete(null);
  };

  const closeEditModal = () => {
    setMappingToEdit(null);
  };

  // Get all unique entity names for the filter dropdown
  const uniqueEntityNames = Array.from(
    new Set(data?.map((mapping) => mapping.entityName) || [])
  ).sort();

  // Pagination
  const totalItems = data?.length || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = data?.slice(startIndex, endIndex);

  const syncModes = [
    { value: "all_sync_modes", label: "All Sync Modes" },
    { value: "IfEmpty", label: "If Empty" },
    { value: "Always", label: "Always" },
    { value: "Ignore", label: "Ignore" },
    { value: "Custom", label: "Custom" },
  ];

  if (isError) {
    return (
      <div className="bg-white dark:bg-zinc-900 rounded-lg shadow p-6 mb-6">
        <h3 className="text-red-500 dark:text-red-400">Error loading field mappings: {String(error)}</h3>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-zinc-900 rounded-lg shadow p-6 mb-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold dark:text-white">Field Mappings</h2>
        <div className="flex space-x-4">
          <div>
            <Select value={entityFilter} onValueChange={setEntityFilter}>
              <SelectTrigger className="w-[200px] dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-100">
                <SelectValue placeholder="Filter by entity" />
              </SelectTrigger>
              <SelectContent className="dark:bg-zinc-800 dark:text-zinc-100">
                <SelectItem value="all_entities">All Entities</SelectItem>
                {uniqueEntityNames.map((entity) => (
                  <SelectItem key={entity} value={entity} className="dark:bg-zinc-800 dark:text-zinc-100">
                    {entity}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Select value={syncModeFilter} onValueChange={setSyncModeFilter}>
              <SelectTrigger className="w-[200px] dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-100">
                <SelectValue placeholder="Filter by sync mode" />
              </SelectTrigger>
              <SelectContent className="dark:bg-zinc-800 dark:text-zinc-100">
                {syncModes.map((mode) => (
                  <SelectItem key={mode.value} value={mode.value} className="dark:bg-zinc-800 dark:text-zinc-100">
                    {mode.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={onAddMapping} className="flex gap-1 dark:bg-zinc-700 dark:text-zinc-100 dark:hover:bg-zinc-600">
            <Plus size={16} />
            Add Mapping
          </Button>
        </div>
      </div>

      <div className="rounded-md border dark:border-zinc-700">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50 dark:bg-zinc-800">
              <TableHead className="dark:text-zinc-100">Entity</TableHead>
              <TableHead className="dark:text-zinc-100">KUP Field</TableHead>
              <TableHead className="dark:text-zinc-100">HS Field</TableHead>
              <TableHead className="dark:text-zinc-100">Type</TableHead>
              <TableHead className="dark:text-zinc-100">Sync Mode</TableHead>
              <TableHead className="text-right dark:text-zinc-100">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array(5)
                .fill(0)
                .map((_, i) => (
                  <TableRow key={i} className="dark:bg-zinc-900">
                    <TableCell>
                      <Skeleton className="h-4 w-20 dark:bg-zinc-700" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-32 dark:bg-zinc-700" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-32 dark:bg-zinc-700" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-24 dark:bg-zinc-700" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-20 dark:bg-zinc-700" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-16 dark:bg-zinc-700" />
                    </TableCell>
                  </TableRow>
                ))
            ) : paginatedData?.length === 0 ? (
              <TableRow className="dark:bg-zinc-900">
                <TableCell colSpan={6} className="text-center py-4 dark:text-zinc-300">
                  No field mappings found
                </TableCell>
              </TableRow>
            ) : (
              paginatedData?.map((mapping) => (
                <TableRow key={`${mapping.entityName}_${mapping.kupField}`} className="dark:bg-zinc-900">
                  <TableCell className="dark:text-zinc-100">{mapping.entityName}</TableCell>
                  <TableCell className="dark:text-zinc-100">{mapping.kupField}</TableCell>
                  <TableCell className="dark:text-zinc-100">{mapping.hsField}</TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={getFieldTypeClass(mapping.isChoiceField ? "choice" : "text") + " dark:border-zinc-600 dark:text-zinc-200"}
                    >
                      {mapping.isChoiceField ? "Choice" : "Text"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={getSyncModeClass(mapping.syncMode) + " dark:border-zinc-600 dark:text-zinc-200"}
                    >
                      {mapping.syncMode === "ifEmpty"
                        ? "If Empty"
                        : mapping.syncMode === "always"
                        ? "Always"
                        : mapping.syncMode === "ignore"
                        ? "Ignore"
                        : "Custom"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEditClick(mapping)}
                      className="dark:text-zinc-300 dark:hover:text-white"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteClick(mapping)}
                      className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {data && data.length > itemsPerPage && (
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-zinc-800">
            <div className="text-sm text-gray-700 dark:text-zinc-200">
              Showing <span className="font-medium">{startIndex + 1}</span> to{" "}
              <span className="font-medium">
                {Math.min(endIndex, totalItems)}
              </span>{" "}
              of <span className="font-medium">{totalItems}</span> results
            </div>
            <div className="flex-1 flex justify-end">
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage <= 1}
                  className="dark:bg-zinc-700 dark:text-zinc-100 dark:border-zinc-600"
                >
                  <span className="sr-only">Go to first page</span>
                  <ChevronLeft className="h-4 w-4" />
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1}
                  className="dark:bg-zinc-700 dark:text-zinc-100 dark:border-zinc-600"
                >
                  <span className="sr-only">Go to previous page</span>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className="dark:bg-zinc-700 dark:text-zinc-100 dark:border-zinc-600"
                >
                  <span className="sr-only">Go to next page</span>
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage >= totalPages}
                  className="dark:bg-zinc-700 dark:text-zinc-100 dark:border-zinc-600"
                >
                  <span className="sr-only">Go to last page</span>
                  <ChevronRight className="h-4 w-4" />
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {mappingToDelete && (
        <AlertDialog open={!!mappingToDelete} onOpenChange={closeDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete the mapping for{" "}
                <strong>
                  {mappingToDelete.entityName}.{mappingToDelete.kupField}
                </strong>
                ? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmDelete}>
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {mappingToEdit && (
        <FieldMappingForm
          mapping={mappingToEdit}
          isOpen={!!mappingToEdit}
          onClose={closeEditModal}
          onSuccess={() => {
            queryClient.invalidateQueries({
              queryKey: ["/api/field-mappings"],
            });
            queryClient.invalidateQueries({
              queryKey: ["/api/field-mappings/all"],
            });
            queryClient.invalidateQueries({
              queryKey: ["/api/field-mappings/statistics"],
            });
            closeEditModal();
          }}
        />
      )}
    </div>
  );
}
